/**
 * Non-destructive re-layout: when the gutter, page size, or panel template
 * changes, existing panels keep their art, lettering, and styling.
 */

import { withRect } from "./panelGeometry";

const READING_ROW_THRESHOLD = 20;
const EDGE_TOLERANCE = 1;
const GEOMETRY_TOLERANCE = 0.5;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/** Panels sorted top-to-bottom, then left-to-right within a row. */
export const sortReadingOrder = (cells) =>
  [...cells].sort((a, b) => {
    const rowDiff = a.y - b.y;
    if (Math.abs(rowDiff) > READING_ROW_THRESHOLD) return rowDiff;
    return a.x - b.x;
  });

/** True when both sets contain the same panel ids at the same rects. */
export const sameGeometry = (cellsA, cellsB) => {
  if (cellsA.length !== cellsB.length) return false;
  return cellsA.every((a) => {
    const b = cellsB.find((c) => c.id === a.id);
    return b
      && Math.abs(a.x - b.x) <= GEOMETRY_TOLERANCE
      && Math.abs(a.y - b.y) <= GEOMETRY_TOLERANCE
      && Math.abs(a.width - b.width) <= GEOMETRY_TOLERANCE
      && Math.abs(a.height - b.height) <= GEOMETRY_TOLERANCE;
  });
};

/**
 * Re-position a panel's items for a new panel size. Positions scale with the
 * panel; bubbles keep their size unless they would no longer fit.
 */
export const scaleItemsToCell = (items, fromSize, toSize) => {
  if (fromSize.width === toSize.width && fromSize.height === toSize.height) return items;
  const sx = fromSize.width > 0 ? toSize.width / fromSize.width : 1;
  const sy = fromSize.height > 0 ? toSize.height / fromSize.height : 1;

  return items.map((item) => {
    if (item.type === "background" || item.x == null || item.y == null) return item;
    if (item.type === "speechBubble") {
      const width = Math.min(item.width ?? 160, toSize.width);
      const height = Math.min(item.height ?? 96, toSize.height);
      return {
        ...item,
        width,
        height,
        x: clamp(item.x * sx, 0, toSize.width - width),
        y: clamp(item.y * sy, 0, toSize.height - height),
      };
    }
    return {
      ...item,
      x: clamp(item.x * sx, 0, toSize.width - 40),
      y: clamp(item.y * sy, 0, toSize.height - 40),
    };
  });
};

/**
 * Move each old panel's content and styling into the new panel at the same
 * reading-order position. Returns how many content-bearing panels had no
 * destination (the new layout has fewer panels).
 */
export const remapIntoCells = (oldCells, newCells) => {
  const oldOrdered = sortReadingOrder(oldCells);
  const newOrdered = sortReadingOrder(newCells);

  const cells = newCells.map((newCell) => {
    const source = oldOrdered[newOrdered.indexOf(newCell)];
    if (!source) return newCell;
    // Geometry comes from the new panel; everything else (items, borders,
    // screentone, shape…) carries over. Merge history no longer applies.
    // (Angled outlines belong to the old geometry too.)
    // eslint-disable-next-line no-unused-vars
    const { id, key, x, y, width, height, mergedFrom, polygon, items, ...styling } = source;
    return {
      ...styling,
      ...newCell,
      items: scaleItemsToCell(items || [], source, newCell),
    };
  });

  const orphanedCount = oldOrdered
    .slice(newOrdered.length)
    .filter((cell) => (cell.items || []).length > 0).length;

  return { cells, orphanedCount };
};

/**
 * Map one axis of a panel rect onto new page geometry. Edges on the page
 * margin stay on the margin; edges facing a neighbour keep the centreline of
 * their gutter, so gutters come out exactly `toGutter` wide and custom panel
 * proportions survive. The mapping composes, so scrubbing a slider is
 * path-independent.
 */
const mapAxis = (start, length, fromLen, toLen, g0, g1) => {
  const end = start + length;
  const scale = fromLen - 2 * g0 > 0 ? (toLen - 2 * g1) / (fromLen - 2 * g0) : 1;
  const mapCentre = (c) => g1 + (c - g0) * scale;

  const nextStart = start <= g0 + EDGE_TOLERANCE ? g1 : mapCentre(start - g0 / 2) + g1 / 2;
  const nextEnd = end >= fromLen - g0 - EDGE_TOLERANCE ? toLen - g1 : mapCentre(end + g0 / 2) - g1 / 2;
  return [nextStart, Math.max(1, nextEnd - nextStart)];
};

// Angled panels rescale their outline into the mapped box
const mapRect = (rect, from, to) => {
  const [x, width] = mapAxis(rect.x, rect.width, from.width, to.width, from.gutter, to.gutter);
  const [y, height] = mapAxis(rect.y, rect.height, from.height, to.height, from.gutter, to.gutter);
  return withRect(rect, { x, y, width, height });
};

/**
 * Reshape hand-adjusted panels in place for a new gutter and/or page size.
 * @param {Array} cells
 * @param {{width:number,height:number,gutter:number}} from
 * @param {{width:number,height:number,gutter:number}} to
 */
export const adjustCellsInPlace = (cells, from, to) =>
  cells.map((cell) => {
    const next = mapRect(cell, from, to);
    return {
      ...next,
      items: scaleItemsToCell(cell.items || [], cell, next),
      ...(cell.mergedFrom
        ? { mergedFrom: cell.mergedFrom.map((rect) => mapRect(rect, from, to)) }
        : {}),
    };
  });
